import type { LeasingReminderSettings, ReminderServiceSettings, ReminderSettings } from '@/types/organizations';

export const DEFAULT_LEASING_MONTHS_AHEAD = 6;

export const DEFAULT_REMINDER_SETTINGS: ReminderSettings = {
  inspection: { enabled: false, notify: false },
  tax: { enabled: false, notify: false },
  leasing: { enabled: true, notify: false, monthsAhead: DEFAULT_LEASING_MONTHS_AHEAD },
};

function parseService(raw: unknown): ReminderServiceSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_REMINDER_SETTINGS.inspection };
  const row = raw as { enabled?: unknown; notify?: unknown };
  const enabled = row.enabled === true;
  return { enabled, notify: enabled && row.notify === true };
}

function clampMonths(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_LEASING_MONTHS_AHEAD;
  return Math.min(24, Math.max(1, Math.round(n)));
}

function parseLeasing(raw: unknown): LeasingReminderSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_REMINDER_SETTINGS.leasing };
  const row = raw as { enabled?: unknown; notify?: unknown; monthsAhead?: unknown };
  const enabled = row.enabled !== false;
  return {
    enabled,
    notify: enabled && row.notify === true,
    monthsAhead: clampMonths(row.monthsAhead),
  };
}

export function mergeReminderOrgSettings(raw?: ReminderSettings | string | null): ReminderSettings {
  let v: unknown = raw ?? null;
  if (typeof raw === 'string') {
    try {
      v = JSON.parse(raw);
    } catch {
      return {
        ...DEFAULT_REMINDER_SETTINGS,
        leasing: { ...DEFAULT_REMINDER_SETTINGS.leasing },
      };
    }
  }
  if (!v || typeof v !== 'object') {
    return {
      ...DEFAULT_REMINDER_SETTINGS,
      leasing: { ...DEFAULT_REMINDER_SETTINGS.leasing },
    };
  }
  const obj = v as { inspection?: unknown; tax?: unknown; leasing?: unknown };
  return {
    inspection: parseService(obj.inspection),
    tax: parseService(obj.tax),
    leasing: parseLeasing(obj.leasing),
  };
}
